import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, Award, Lightbulb, Zap, Handshake, DollarSign } from "lucide-react";

const ICONS = { users: Users, lightbulb: Lightbulb, zap: Zap, "dollar-sign": DollarSign, handshake: Handshake, award: Award };

export const EXCHANGE_DEFAULT_DATA = {
  hero: {
    logo_image: "/api/media/0cad1b27-c57e-4087-b9a2-e0d50e8045fa",
    title_prefix: "Antera Exchange,",
    title_accent: "Connect & Learn",
    tagline: "Antera Software fosters a unique environment for professional growth and operational excellence through its Antera Exchange and Connect & Learn initiatives. These programs transform the software experience from a simple tool into a collaborative partnership focused on long-term success.",
    primary_label: "View Events",
    primary_href: "/events",
    secondary_label: "Elite Supplier Program",
    secondary_href: "/exchange/elite-supplier",
    image: "/api/media/f521692c-10d7-4e23-9c68-61e1f53c9e14",
  },
  community: {
    image: "/api/media/0f246c39-40c2-4e24-8936-c56fada90cde",
    heading: "Collaborative Events & Functional User Groups",
    subheading: "Join us for sales meetings, training workshops, and user group sessions",
    sections: [
      { icon: "users", title: "Strategic Collaborative Learning", bullets: [
        {lead: "Like-Minded Community:", body: "Join a network of industry professionals who prioritize scalable infrastructure and advanced automation as drivers for their growth."},
        {lead: "Virtual and In-Person Access:", body: "Whether through sales meetings or user group sessions, these events provide flexible ways to engage with the Antera team and other users."},
        {lead: "Consultative Environment:", body: "These sessions mirror Antera's partnership-driven approach, moving beyond simple software installation to help you actively transform your business."},
      ]},
      { icon: "lightbulb", title: "Tailored Professional Development", bullets: [
        {lead: "Role-Based Training Workshops:", body: "Participate in workshops that provide modular, specialized training relevant to your specific duties, such as CRM for sales reps or accounting integrations for finance users."},
        {lead: "Direct Expert Access:", body: "Engage with a support team that possesses deep industry knowledge regarding decoration nuances, PromoStandards data, and complex order management."},
        {lead: "Workflow Mapping:", body: "Learn how to better align your specific order fulfillment processes - from quoting to final invoicing - with the platform's Dynamic Workflow Engine."},
      ]},
      { icon: "zap", title: "Innovation and Future-Proofing", bullets: [
        {lead: "Insights into New Tools:", body: "Be among the first to learn how to leverage the new suite of AI-enhanced tools, such as Brand Assist and Order Assist, to move your team from clerical execution to strategic selling."},
        {lead: "Direct Feedback Loop:", body: "As a family-owned company, Antera drives innovation based on actual client needs rather than external shareholder demands, making these community events vital for future product development."},
        {lead: "Scalability Best Practices:", body: "Gain insights into how to use the platform to manage business growth, moving from entry-level volumes to enterprise-level sales with ease."},
      ]},
    ],
    cta_label: "Join a User Group",
    cta_href: "/exchange/user-groups",
  },
  elite: {
    image: "/api/media/b99cd589-ddd4-45e6-816e-9c79562c9ab1",
    heading: "Elite Supplier Program",
    tagline: "The Antera Elite Supplier Program is a strategic partnership designed to accelerate growth and operational efficiency by creating an integrated, efficient supply chain experience.",
    sections: [
      { icon: "dollar-sign", title: "Competitive & Financial Advantages", bullets: [
        {lead: "Single Pricing Benefit:", body: "Elite Suppliers provide high-value pricing benefits such as standard discounts, EQP, or specialized program pricing per qualified invoice."},
        {lead: "Preferential Services:", body: "Enrolled businesses may receive competitive advantages, including special product pricing, dedicated support team members, special net terms, and complimentary samples."},
      ]},
      { icon: "zap", title: "Operational Efficiency", bullets: [
        {lead: "Accelerated Workflows:", body: "Participants benefit from accelerated quoting and simplified order processing, which maximizes overall profitability and efficiency."},
        {lead: "Seamless System Usage:", body: "By initiating purchase orders and vouching invoices directly within the Advance system, businesses ensure their financial data is accurate and ready for rebate calculations."},
      ]},
      { icon: "handshake", title: "Strategic Relationships & Support", bullets: [
        {lead: "Relationship Building:", body: "The program encourages active engagement between participants and supplier sales representatives to build strong sales relationships."},
        {lead: "Community Involvement:", body: "Enrollment includes participation in Antera Connect & Learn Events, joining a network of like-minded professionals for virtual and in-person learning."},
      ]},
    ],
    primary_label: "Learn More",
    primary_href: "/exchange/elite-supplier",
    secondary_label: "Interested in Being an Elite Supplier?",
    secondary_href: "https://lp.constantcontactpages.com/sl/IU4ylcE/InterestedinbeinganElite",
  },
  bottom_cta: {
    heading: "Unlock Explosive Growth",
    subheading: "Discover how to move beyond entry-level solutions and implement the robust, scalable platform designed to manage and fuel your growth.",
    button_label: "Request a Demo",
    button_href: "/contact",
  },
};

function IconedSection({ icon, title, bullets }) {
  const Icon = ICONS[icon] || Users;
  return (
    <div>
      <h3 className="text-base font-bold text-slate-900 flex items-center gap-2 mb-3">
        <Icon className="h-5 w-5 text-sky-600" /> {title}
      </h3>
      <ul className="space-y-2.5 text-sm text-slate-600">
        {(bullets || []).map((b, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-500 mt-1.5 shrink-0" />
            <span><strong>{b.lead}</strong> {b.body}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ExchangePage({ data }) {
  const d = { ...EXCHANGE_DEFAULT_DATA, ...(data || {}) };
  const hero = { ...EXCHANGE_DEFAULT_DATA.hero, ...(d.hero || {}) };
  const community = { ...EXCHANGE_DEFAULT_DATA.community, ...(d.community || {}) };
  const elite = { ...EXCHANGE_DEFAULT_DATA.elite, ...(d.elite || {}) };
  const bottom = { ...EXCHANGE_DEFAULT_DATA.bottom_cta, ...(d.bottom_cta || {}) };

  return (
    <div data-testid="exchange-page" className="page-transition">
      <section className="pt-32 pb-14 lg:pt-40 lg:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                {hero.logo_image ? (
                  <div className="flex items-center gap-3 mb-6">
                    <img src={hero.logo_image} alt="Antera Exchange" className="h-12 w-auto" />
                  </div>
                ) : null}
                <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
                  {hero.title_prefix} <span className="text-sky-600">{hero.title_accent}</span>
                </h1>
                <p className="mt-5 text-base lg:text-lg text-slate-500 leading-relaxed">{hero.tagline}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  {hero.primary_label ? (
                    <Link to={hero.primary_href || "/events"}>
                      <Button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-6 h-11 text-sm font-semibold">
                        {hero.primary_label} <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  ) : null}
                  {hero.secondary_label ? (
                    <Link to={hero.secondary_href || "/exchange/elite-supplier"}>
                      <Button variant="outline" className="rounded-full px-6 h-11 text-sm font-semibold border-slate-300">
                        {hero.secondary_label}
                      </Button>
                    </Link>
                  ) : null}
                </div>
              </div>
              {hero.image ? (
                <div className="rounded-xl overflow-hidden shadow-lg border border-slate-200">
                  <img src={hero.image} alt="Antera Exchange" className="w-full h-auto" />
                </div>
              ) : null}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {community.image ? (
              <div className="rounded-xl overflow-hidden shadow-lg border border-slate-200">
                <img src={community.image} alt="Collaborative Events" className="w-full h-72 lg:h-96 object-cover" />
              </div>
            ) : null}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-2">{community.heading}</h2>
              <p className="text-sm text-slate-500 mb-8">{community.subheading}</p>
              <div className="space-y-8">
                {(community.sections || []).map((s, i) => <IconedSection key={i} {...s} />)}
              </div>
              {community.cta_label ? (
                <Link to={community.cta_href || "/exchange/user-groups"} className="inline-block mt-8">
                  <Button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-6 h-11 text-sm font-semibold">
                    {community.cta_label} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {elite.image ? (
              <div className="lg:order-2 rounded-xl overflow-hidden shadow-lg border border-slate-200">
                <img src={elite.image} alt="Elite Supplier Program" className="w-full h-72 lg:h-96 object-cover" />
              </div>
            ) : null}
            <div className="lg:order-1">
              <div className="flex items-center gap-2 mb-4">
                <Award className="h-6 w-6 text-sky-600" />
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{elite.heading}</h2>
              </div>
              <p className="text-base text-slate-500 leading-relaxed mb-8">{elite.tagline}</p>
              <div className="space-y-8">
                {(elite.sections || []).map((s, i) => <IconedSection key={i} {...s} />)}
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                {elite.primary_label ? (
                  <Link to={elite.primary_href || "/exchange/elite-supplier"}>
                    <Button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-6 h-11 text-sm font-semibold">
                      {elite.primary_label} <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                ) : null}
                {elite.secondary_label ? (
                  <a href={elite.secondary_href || "#"} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="rounded-full px-6 h-11 text-sm font-semibold border-slate-300">
                      {elite.secondary_label}
                    </Button>
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-900 py-14 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">{bottom.heading}</h2>
          <p className="mt-3 text-sm text-slate-400 max-w-lg mx-auto">{bottom.subheading}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={bottom.button_href || "/contact"}>
              <Button className="bg-sky-500 hover:bg-sky-400 text-white rounded-full px-8 h-12 text-sm font-semibold">
                {bottom.button_label} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
